import { FC, useMemo } from "react";

import {
  formatPrice,
  isNullOrEmptySpace,
  NO_VALUE_DISPLAY,
} from "@ryde/shared";

import { FramerTooltip } from "../../common";
import { useDecorateWithMark } from "../../utils";
import { IRenderColumnTemplate } from "./_core";

// Dedicated cell template for currency/discount values
// Expects either a number or an object: { value: number, isCurrency?: boolean }
export const DiscountCellTemplate: FC<IRenderColumnTemplate> = ({
  id,
  value,
  name,
  formFieldTypeId,
}) => {
  const { decorateValue } = useDecorateWithMark();

  const { numericValue, isCurrency } = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const v: any = value;
    if (typeof v === "object" && v !== null && typeof v.value === "number") {
      return {
        numericValue: v.value as number,
        isCurrency: Boolean(v.isCurrency),
      };
    }

    if (typeof v === "number") {
      return { numericValue: v as number, isCurrency: true };
    }

    return { numericValue: undefined as unknown as number, isCurrency: false };
  }, [value]);

  const displayValue = useMemo(() => {
    if (numericValue === 0 || Number.isFinite(numericValue)) {
      const base = isCurrency
        ? formatPrice({ value: numericValue, isCurrency: true })
        : `${numericValue}%`;
      return decorateValue(base);
    }

    return "";
  }, [numericValue, isCurrency, decorateValue]);

  const tooltipContent = useMemo(() => {
    if (numericValue === 0 || Number.isFinite(numericValue)) {
      return isCurrency
        ? formatPrice({ value: numericValue, isCurrency: true })
        : `${numericValue}%`;
    }

    return NO_VALUE_DISPLAY;
  }, [numericValue, isCurrency]);

  const isEmpty = useMemo(() => {
    // Consider null/undefined/empty string as empty; 0 is NOT empty
    return (
      value === null ||
      value === undefined ||
      value === "" ||
      isNullOrEmptySpace(value)
    );
  }, [value]);

  return (
    <div
      className="cell_simple"
      data-e2e-id={`${name}_fieldid_${formFieldTypeId}`}
      data-e2e-value={value}
      data-e2e-template="discount-cell"
      data-e2e-custom-field-id={id}
    >
      <FramerTooltip content={tooltipContent} rtlStrippedBottomLeftPlacement>
        <span>{!isEmpty ? displayValue : NO_VALUE_DISPLAY}</span>
      </FramerTooltip>
    </div>
  );
};
